import { isNaN } from 'lodash';

const toDate = (input) => {
  if (input instanceof Date) return input;

  // allow timestamp numbers (epoch)
  if (typeof input === 'number') return new Date(input);

  // allow numeric-string timestamps as fallback
  if (typeof input === 'string' && /^\d+$/.test(input.trim())) {
    const n = Number(input);
    return new Date(n.toString().length === 10 ? n * 1000 : n);
  }

  return null;
};

export const format = (input, pattern) => {
  const date = toDate(input);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const pad = (num, size = 2) => num.toString().padStart(size, '0');

  const replacements = {
    yyyy: date.getFullYear(),
    MM: pad(date.getMonth() + 1),
    M: date.getMonth() + 1,
    dd: pad(date.getDate()),
    d: date.getDate(),
    HH: pad(date.getHours()),
    H: date.getHours(),
    mm: pad(date.getMinutes()),
    m: date.getMinutes(),
    ss: pad(date.getSeconds()),
    s: date.getSeconds(),
  };

  return pattern.replace(
    /(yyyy|MM|M|dd|d|HH|H|mm|m|ss|s)/g,
    token => replacements[token] ?? token,
  );
};

export const formatInTimeZone = (input, pattern, timeZone) => {
  const date = toDate(input);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = dtf.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  const tzDate = new Date(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`,
  );

  return format(tzDate, pattern);
};

export const parseISO = (isoString) => {
  if (typeof isoString !== 'string') return null;

  const normalized = isoString.trim().replace(' ', 'T');

  const date = new Date(normalized);

  return isNaN(date) ? null : date;
};

export const startOfDay = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const endOfDay = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

export const isMatch = (dateString, pattern) => {
  if (typeof dateString !== 'string') return false;

  const regex = pattern
    .replace(/yyyy/g, '\\d{4}')
    .replace(/MM/g, '\\d{2}')
    .replace(/M/g, '\\d{1,2}')
    .replace(/dd/g, '\\d{2}')
    .replace(/d/g, '\\d{1,2}')
    .replace(/HH/g, '\\d{2}')
    .replace(/H/g, '\\d{1,2}')
    .replace(/mm/g, '\\d{2}')
    .replace(/m/g, '\\d{1,2}')
    .replace(/ss/g, '\\d{2}')
    .replace(/s/g, '\\d{1,2}')
    .replace(/[-.: ]/g, m => `\\${m}`);

  const re = new RegExp(`^${regex}$`);
  return re.test(dateString);
};
